import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../../../../../services/api";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import cloneDeep from "lodash/cloneDeep";
import SyncLoader from "react-spinners/SyncLoader";
import * as Dialog from "@radix-ui/react-dialog";
import { format } from "date-fns";
import { useAuth } from "../../../../../../hooks/auth";
import { debounce } from "lodash";
import { useRoutes } from "../../../../../../hooks/routes";

import IHookForm from "../../../../../../shared/dtos/IHookForm";
import IListTemplateCategoryApiProps from "./dtos/IListTemplateCategoryApiProps";
import IListTemplateCategoryDataParams from "./dtos/IListTemplateCategoryDataParams";
import IFormSearch from "./dtos/IFormSearch";

import MasterAsideControls from "../../../../components/SidebarMaster/MasterAsideControls";
import HeaderMaster from "../../../../components/SidebarMaster/HeaderMaster";
import MasterContainer from "../../../../components/SidebarMaster/MasterContainer";
import MasterMain from "../../../../components/SidebarMaster/MasterMain";

import DialogModal from "../../../../../../shared/components/DialogModal";
import Button from "../../../../../../shared/components/Button";
import Input from "../../../../../../shared/components/Input";
import IconSvg from "../../../../../../shared/components/IconSvg";
import Pagination from "../../../../../../shared/components/Pagination";
import Tooltip from "../../../../../../shared/components/Tooltip";

import penIcon from "../../../../../../shared/assets/pen-icon.svg";
import trashIcon from "../../../../../../shared/assets/trash-icon.svg";
import iconSearchWhite from "../../../../../../shared/assets/icon-search-white.svg";

import { Notify } from "../../../../../../shared/utils";

import {
  BoxList,
  ButtonFlexBox,
  ButtonSearch,
  FlexBox,
  HStack,
  Main,
  Form,
  ContainerCreate,
  FlexPagination,
  ContainerLoading,
  Table,
  TableTh,
  TableTr,
  TableTd,
  TableThead,
  TableTbody,
  TableTdText,
} from "./styles";

const ListTemplateCategory: React.FC = () => {
  const location = useLocation();
  const dataLocation: any = location.state;

  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [dataTemplateCategoryList, setDataTemplateCategoryList] =
    useState<IListTemplateCategoryApiProps>({
      pagination: 0,
      dataArray: [],
    });
  const [listTemplateCategoryDataParams, setListTemplateCategoryDataParams] =
    useState<IListTemplateCategoryDataParams>({
      pagination:
        dataLocation && dataLocation.params != null
          ? dataLocation.params.pagination
          : 0,
      is_dash:
        dataLocation && dataLocation.params != null
          ? dataLocation.params.is_dash
          : true,
    });

  const { data: dataUseAuth } = useAuth();

  const { getUrl } = useRoutes();

  const userPermissionCreate =
    dataUseAuth.admin_user_permissions.create_template_categories;
  const userPermissionRead =
    dataUseAuth.admin_user_permissions.read_template_categories;
  const userPermissionUpdate =
    dataUseAuth.admin_user_permissions.update_template_categories;
  const userPermissionDelete =
    dataUseAuth.admin_user_permissions.delete_template_categories;

  const navigate = useNavigate();

  useEffect(() => {
    getUrl("/template-category");
  }, []);

  const handleCreateTemplateCategory = useCallback(() => {
    navigate("/create-template-category", {
      state: { params: listTemplateCategoryDataParams },
    });
  }, [navigate, listTemplateCategoryDataParams]);

  useEffect(() => {
    (async () => {
      const response = await api.get("/bits-template-categories", {
        params: listTemplateCategoryDataParams,
      });

      setDataTemplateCategoryList(response.data);
      setTotalPages(response.data.pagination);
      setLoading(false);
    })();
  }, [reload, listTemplateCategoryDataParams]);

  const handleDeleteTemplateCategory = useCallback(
    async (id: string) => {
      await api.delete(`/bits-template-categories/${id}`);
      setReload(!reload);
    },
    [reload]
  );

  const formSchema = yup.object().shape({
    search: yup.string(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IHookForm>({
    mode: "onChange",
    resolver: yupResolver(formSchema),
  });

  const onSubmit = useCallback(
    debounce((formData: string) => {
      try {
        setLoading(true);

        if (formData === "") {
          setListTemplateCategoryDataParams({
            pagination: 0,
            is_dash: true,
          });
        } else {
          setListTemplateCategoryDataParams((state) => {
            return {
              pagination: 0,
              is_dash: true,
              search: formData,
            };
          });
        }
      } catch (err: any) {
        console.log(err?.response?.data);
      }
    }, 400),
    []
  );

  const navigateView = useCallback(
    (item) => {
      navigate(`/view-template-category/${item.id}`, {
        state: { params: listTemplateCategoryDataParams },
      });
    },
    [navigate, listTemplateCategoryDataParams]
  );

  const notifyRead = useCallback(() => {
    Notify({ text: "Você não tem permissão de visualizar", type: "error" });
  }, []);

  const handleInputSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchTerm = event.target.value;

      onSubmit(searchTerm);
    },
    [onSubmit]
  );

  return (
    <MasterContainer>
      <MasterAsideControls />
      <HeaderMaster namePage="Lista de categorias de templates" />
      <MasterMain>
        <Main>
          <ContainerCreate>
            <Button
              onClick={handleCreateTemplateCategory}
              disabled={!userPermissionCreate}
            >
              Criar categoria de template
            </Button>
          </ContainerCreate>

          <HStack>
            <Input
              onChangeInput={handleInputSearchChange}
              label="search"
              register={register}
              required={true}
              placeholder="Pesquisar"
              style={{ width: "100%" }}
            />
          </HStack>

          <BoxList>
            {loading && (
              <ContainerLoading>
                <SyncLoader loading={loading} color="#663399" size={10} />
              </ContainerLoading>
            )}

            {!loading && (
              <Table>
                <TableThead>
                  <TableTr>
                    <TableTh>Categorias de template</TableTh>
                    <TableTh>Data de criação</TableTh>
                    <TableTh>Última modificação</TableTh>
                    <TableTh>Opções</TableTh>
                  </TableTr>
                </TableThead>

                <TableTbody>
                  {dataTemplateCategoryList.dataArray.map((item, index) => (
                    <TableTr key={item.id}>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>{item.title}</TableTdText>
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>
                          {format(
                            new Date(item.created_at),
                            "dd/MM/yyyy HH:mm"
                          )}
                        </TableTdText>
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>
                          {format(
                            new Date(item.updated_at),
                            "dd/MM/yyyy HH:mm"
                          )}
                        </TableTdText>
                      </TableTd>

                      <TableTd>
                        <FlexBox>
                          <Tooltip title="Editar categoria" placement="top">
                            <ButtonFlexBox
                              disabled={!userPermissionUpdate}
                              onClick={() => {
                                navigate(`/edit-template-category/${item.id}`, {
                                  state: {
                                    params: listTemplateCategoryDataParams,
                                  },
                                });
                              }}
                            >
                              <IconSvg width="18" height="18" image={penIcon} />
                            </ButtonFlexBox>
                          </Tooltip>

                          <Dialog.Root>
                            <Tooltip title="Deletar categoria" placement="top">
                              <Dialog.Trigger asChild>
                                <ButtonFlexBox disabled={!userPermissionDelete}>
                                  <IconSvg
                                    width="18"
                                    height="18"
                                    image={trashIcon}
                                  />
                                </ButtonFlexBox>
                              </Dialog.Trigger>
                            </Tooltip>

                            <DialogModal
                              title="Deletar categoria de template"
                              subtitle={item.title}
                              type="delete"
                              callback={() =>
                                handleDeleteTemplateCategory(item.id)
                              }
                            />
                          </Dialog.Root>
                        </FlexBox>
                      </TableTd>
                    </TableTr>
                  ))}
                </TableTbody>
              </Table>
            )}
          </BoxList>
          <FlexPagination>
            {dataTemplateCategoryList.dataArray.length > 0 && (
              <Pagination
                total={totalPages}
                offset={listTemplateCategoryDataParams.pagination}
                setDataParams={setListTemplateCategoryDataParams}
                dataParams={listTemplateCategoryDataParams}
              />
            )}
          </FlexPagination>
        </Main>
      </MasterMain>
    </MasterContainer>
  );
};

export default ListTemplateCategory;
